import { computed, inject, Injectable, Signal } from '@angular/core';
import {
  CompanySetupStatusEnum,
  IReportJwtPayload,
  IReportPackageEnum,
  UserProfileSetupStatusEnum,
} from '../utils/JwtUtils';
import { StorageService } from './storage.service';

export type Permission =
  | 'CompanyManager'
  | 'ProjectManager'
  | 'CompanyAdmin'
  | 'ProjectAdmin'
  | 'EmployeeAdmin'
  | 'CustomerAdmin'
  | 'ActivityTypeAdmin'
  | 'HolidayAdmin'
  | 'Reporting'
  | 'Invoicing'
  | 'Billing'
  | 'AbsenceTypeAdmin'
  | 'WorkingTimeReporting'
  | 'Validation'
  | 'ExpenseTypeAdmin';

export const PermissionTranslations: { [key in Permission]: string } = {
  CompanyManager: 'FirmaVerwalter',
  ProjectManager: 'ProjektVerwalter',
  CompanyAdmin: 'FirmaAdmin',
  ProjectAdmin: 'ProjektAdmin',
  EmployeeAdmin: 'MitarbeiterAdmin',
  CustomerAdmin: 'KundeAdmin',
  ActivityTypeAdmin: 'LeistungsartAdmin',
  HolidayAdmin: 'FeiertagAdmin',
  Reporting: 'Rapportierung',
  Invoicing: 'Fakturierung',
  Billing: 'Abrechnung',
  AbsenceTypeAdmin: 'AbwesenheitsartAdmin',
  WorkingTimeReporting: 'Arbeitszeitauswertung',
  Validation: 'Validierung',
  ExpenseTypeAdmin: 'SpesenartAdmin',
};

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private storageService = inject(StorageService);

  public jwt = computed<IReportJwtPayload | null>(() =>
    this.storageService.jwt(),
  );

  // Computed signal for roles - reacts automatically to StorageService token changes
  public userPermissions: Signal<string> = computed(
    () => this.jwt()?.role ?? '',
  );

  public isProPackage = computed(
    () => this.jwt()?.ireportpackage === IReportPackageEnum.Pro.toString(),
  );

  // PBI #2222/#2223: Zugriffsbeschraenkung auf die interne Firmen-/
  // Trial-Uebersicht (Admin-Dashboard) - siehe IST-Doku 6.32/6.33.
  // Firma 1 = "iServices AG" (siehe Backend CompanyOverviewController,
  // Konstante IServicesFirmaId).
  public isIServicesAdmin = computed(() => {
    const jwt = this.jwt();
    return (
      jwt?.companyid === '1' &&
      (jwt?.email?.toLowerCase().endsWith('@iservices.ch') ?? false)
    );
  });

  public isCompanySetupIncomplete = computed(
    () =>
      this.jwt()?.companystatus === CompanySetupStatusEnum.Pending.toString(),
  );

  public isUserProfileSetupIncomplete = computed(
    () =>
      this.jwt()?.userstatus === UserProfileSetupStatusEnum.Pending.toString(),
  );

  public isTestDataActive = computed(
    () => (this.jwt()?.testdataactive?.toLowerCase() ?? 'false') === 'true',
  );

  public hasOneOfPermissions(...permissions: Permission[]): boolean {
    for (var permission of permissions) {
      if (
        this.userPermissions()?.includes(PermissionTranslations[permission]) ??
        false
      ) {
        return true;
      }
    }

    return false;
  }
}
